import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Trash2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SeoResultCard, SeoResult } from "@/components/SeoResultCard";
import { toast } from "sonner";
import { Link } from "react-router-dom";

type Row = {
  id: string;
  product_name: string;
  seo_title: string;
  seo_description: string;
  keywords: string[];
  tags: string[];
  seo_score: number;
  created_at: string;
};

export default function History() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Row | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("seo_results")
      .select("id, product_name, seo_title, seo_description, keywords, tags, seo_score, created_at")
      .order("created_at", { ascending: false });
    setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id: string) => {
    const { error } = await supabase.from("seo_results").delete().eq("id", id);
    if (error) return toast.error("Failed to delete");
    toast.success("Deleted");
    setRows((p) => p.filter((r) => r.id !== id));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">History</h1>
        <p className="text-muted-foreground">All your generated SEO listings.</p>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : rows.length === 0 ? (
        <Card className="bg-gradient-card border-border/50 border-dashed">
          <CardContent className="text-center py-16 text-muted-foreground">
            <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No history yet</p>
            <Button asChild className="mt-4 bg-gradient-primary"><Link to="/generate">Generate your first</Link></Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {rows.map((r) => (
            <Card key={r.id} className="bg-gradient-card border-border/50 hover:shadow-elegant transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-lg truncate">{r.product_name}</CardTitle>
                    <CardDescription className="truncate mt-0.5">{r.seo_title}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary">{r.seo_score}/100</Badge>
                    <Button size="sm" variant="outline" onClick={() => setOpen(r)}><Eye className="h-3.5 w-3.5 mr-1.5" />View</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>SEO Listing</DialogTitle></DialogHeader>
          {open && (
            <SeoResultCard
              productName={open.product_name}
              result={{
                seo_title: open.seo_title,
                seo_description: open.seo_description,
                keywords: open.keywords as string[],
                tags: open.tags as string[],
                seo_score: open.seo_score,
              } as SeoResult}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
